import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './Tooltip.css';

interface TooltipProps {
  content: string;
  children: React.ReactElement;
  position?: 'top' | 'bottom' | 'left' | 'right';
  delay?: number;
  theme?: 'dark' | 'light';
  disabled?: boolean;
  className?: string;
}

export const Tooltip: React.FC<TooltipProps> = ({ 
  content, 
  children, 
  position = 'top',
  delay = 300,
  theme = 'dark',
  disabled = false,
  className
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const [timeoutId, setTimeoutId] = useState<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [timeoutId]);

  const calculatePosition = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    
    switch (position) {
      case 'top':
        setCoords({ top: rect.top - 8, left: rect.left + rect.width / 2 });
        break;
      case 'bottom':
        setCoords({ top: rect.bottom + 8, left: rect.left + rect.width / 2 });
        break;
      case 'left':
        setCoords({ top: rect.top + rect.height / 2, left: rect.left - 8 });
        break;
      case 'right':
        setCoords({ top: rect.top + rect.height / 2, left: rect.right + 8 });
        break;
    }
  }, [position]);

  const handleMouseEnter = () => {
    if (disabled) return;
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    calculatePosition();
    if (delay > 0) {
      const id = setTimeout(() => {
        setIsVisible(true);
      }, delay);
      setTimeoutId(id);
    } else {
      setIsVisible(true);
    }
  };

  const handleMouseLeave = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      setTimeoutId(null);
    }
    setIsVisible(false);
  };

  const getTooltipStyle = (): React.CSSProperties => {
    const baseStyle: React.CSSProperties = {
      position: 'fixed',
      zIndex: 99999,
      padding: '6px 10px',
      borderRadius: '6px',
      fontSize: '12px',
      fontWeight: 500,
      whiteSpace: 'nowrap',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      pointerEvents: 'none',
      opacity: isVisible ? 1 : 0,
      transition: 'opacity 0.1s ease-in-out',
    };

    const themeStyles = theme === 'dark' 
      ? {
          backgroundColor: '#2D3748',
          color: '#FFFFFF',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        }
      : {
          backgroundColor: '#FFFFFF',
          color: '#2D3748',
          border: '1px solid #E2E8F0',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        };

    const positionStyles = (() => {
      switch (position) {
        case 'top':
          return {
            top: coords.top,
            left: coords.left,
            transform: 'translate(-50%, -100%)',
          };
        case 'bottom':
          return {
            top: coords.top,
            left: coords.left,
            transform: 'translateX(-50%)',
          };
        case 'left':
          return {
            top: coords.top,
            left: coords.left,
            transform: 'translate(-100%, -50%)',
          };
        case 'right':
          return {
            top: coords.top,
            left: coords.left,
            transform: 'translateY(-50%)',
          };
        default:
          return {};
      }
    })();

    return {
      ...baseStyle,
      ...themeStyles,
      ...positionStyles,
    };
  };

  if (disabled) {
    return children;
  }

  return (
    <div 
      ref={containerRef}
      className={`tooltip-trigger ${className || ''}`}
      style={{ position: 'relative', display: 'inline-block' }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      {createPortal(
        <div 
          className={`tooltip-container ${theme === 'dark' ? 'tooltip-dark' : 'tooltip-light'}`}
          style={getTooltipStyle()}
        >
          <div className="tooltip-content">
            {content}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};
